<!--
  Purpose:     Manage calculation properties for field aggregation in a target table.
  Component:   Field Calculation Modal: MDM Aggregation panel.
  Path:        Field - Calculation - MDM Aggregation.
  Calculation:
  - aggregationFieldGuid  Field whose values will be aggregated (in Aggregation Table). [String]
  - aggregateFunction     Function to apply to Aggregation Field. [String]
  - filterFormula         Javascript expression to filter rows in the Aggregation table. [String]
  Helpers:
  - Aggregation Table     Dropdown of all tables other than current.
  - Formula Editor        Code entry control to edit Filter Formula.
  - Table Fields          List of Fields in Aggregation Table that can be embedded in Filter Formula. [Listbox]
  - Row Fields            List of Fields in Target (current) Table Row that can be embedded in Filter Formula (with 'ROW:' prefix). [Listbox]
  - Atlas Assistant       AI Chat service to help generate Filter Formula.
-->
<template>
  <div>
    <!-- First row - Aggregation Field and Aggregation Function. -->
    <div class="row">
      <div class="col-4">
        <b-form-group label="Table to Aggregate">
          <entity-selector ref="entitySelector" id="entitySelector" v-model="aggregationEntityGuid"
            :entityGuidsToIgnore="aggregationEntityGuidsToIgnore" />
        </b-form-group>
      </div>
      <div class="col-4">
        <b-form-group label="Field to Aggregate">
          <b-form-select v-model="calculationData.aggregationFieldGuid" :options="aggregationFieldOptions" />
        </b-form-group>
      </div>
      <div class="col-4">
        <b-form-group label="Aggregate Function">
          <aggregate-function-select :aggregation-field="aggregationField"
            v-model="calculationData.aggregateFunction" />
        </b-form-group>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <!-- Table Field selection. -->
        <b-form-group class="properties_group field-selector" label="Table Fields">
          <field-selector ref="fieldSelector" :table="aggregationEntity" :fields-to-ignore="selectorTableFieldsToIgnore"
            @add-field="addTableFieldToFormula" />
        </b-form-group>
        <!-- Row Field selection. -->
        <b-form-group class="properties_group field-selector" label="Row Fields">
          <field-selector ref="rowFieldSelector" :table="table" :fields-to-ignore="selectorRowFieldsToIgnore"
            @add-field="addRowFieldToFormula" />
        </b-form-group>
      </div>
      <div class="col-8">


        <!-- Formula Editor -->
        <calculation-formula-code-editor id="formulaEditor" ref="formulaEditor" label="Filter Formula"
          language="javascript" :formula="filterFormula" :constructFieldDefinition="constructFieldDefinition"
          @formula-changed="onFormulaChanged" :calculation-warning-on-verify="calculationWarningOnVerify" :atlas-assistant="atlasAssistant" />
        <!--<div class="border mt-1">{{ calculationData.filterFormula }}</div>-->

        <!-- Atlas Assistant -->
        <atlas-assistant ref="atlasAssistant" class="atlasAssistant mt-3 mb-1" query-type="AggregationFilter"
          :target-field="tableField" :query-fields="assistantQueryFields"
          :additionalFunctions="assistantAdditionalFunctions" @use-assistant-formula="useAssistantFormula"
          :log-assistant-conversation="false" />

      </div>
    </div>
  </div>
</template>

<script>

import EntitySelector from '../../components/EntitySelector.vue';
import FieldSelector from '../../components/FieldSelector.vue';
import AggregateFunctionSelect from './AggregateFunctionSelect.vue';
import CalculationFormulaCodeEditor from './CalculationFormulaCodeEditor.vue';
import AtlasAssistant from '../../components/AtlasAssistant.vue';

export default {

  name: 'MdmAggregationPanel',


  components: {
    EntitySelector,
    FieldSelector,
    AggregateFunctionSelect,
    CalculationFormulaCodeEditor,
    AtlasAssistant,
  },

  inject: [
    // Current Table. [from CalculationModel].
    'table',
    // Current Field. [from CalculationModel].
    'tableField',
    // Calculation data - to be populated with Lookup Pairs. [from CalculationModel].
    'calculationData',
    // Clone field with current Calculation properties. [from CalculationModel].
    'constructFieldDefinition',
    // Utility functions etc. [from CalculationModel].
    'apiUtils',
    'entityUtils',
  ],

  props: {},

  data() {
    return {
      control: {
        calculationDataLoaded: false,
        aggregationFieldsLoaded: false,
      },
      /** Identifies the Table selected for Aggregation. */
      aggregationEntityGuid: '',
      /** Aggregation Table. */
      aggregationEntity: null,
      /** Fields from Aggregation Table. */
      aggregationFields: [],
      /** Filter Formula as presented in the Editor. */
      filterFormula: '',
    };
  },

  watch: {

    /** Aggregate Entity shown in Selector. */
    aggregationEntityGuid: {
      handler(newValue, oldValue) {
        // Clear Aggregation Table.
        this.aggregationEntity = null;
        // Clear formula.
        if (oldValue) this.filterFormula = 'Test!';
        // Populate Aggregation Fields.
        this.aggregationFields = [];
        this.control.aggregationFieldsLoaded = false;
        // Query fields for Aggregation Table.
        this.apiUtils.getEntityFields(this.aggregationEntityGuid)
          .then((fields) => {
            // Filter fields to exclude internal.
            this.aggregationFields = (fields || []).filter(fld => !fld.isInternal);
            // Aggregation Table - with fields.
            this.apiUtils.getEntity(this.aggregationEntityGuid)
              .then((entity) => {
                if (entity)
                  this.aggregationEntity = { ...entity, fields: this.aggregationFields };

                // Can now complete component initialisation (where Aggregation Entity details required) if not done previously.
                if (!this.calculationDataLoaded) {
                  // Populate formula to edit with field names.
                  this.filterFormula = this.entityUtils.changeFieldGuidsToNames(this.calculationData.filterFormula, this.aggregationEntity?.fields || [], this.table.fields);
                  this.calculationDataLoaded = true;
                }
              })
              .catch((error) => {
                console.error(`Error loading entity ${this.aggregationEntityGuid}:`, error);
              });
          })
          .catch((error) => {
            console.error(`Error loading fields for entity ${this.aggregationEntityGuid}:`, error);
          });
        this.control.aggregationFieldsLoaded = true;
      },
    },

    'calculationData.aggregationFieldGuid'(vewVal){
      this.clearformulaVerify();
    },

    'calculationData.aggregateFunction'(vewVal){
      this.clearformulaVerify();
    },

  },

  computed: {

    /** Entityies to exclude from Aggregation Entity selection. */
    aggregationEntityGuidsToIgnore() {
      return [this.table.entityGuid];
    },

    /** Fields available for selection as Aggregation Field. */
    aggregationFieldOptions() {
      return (this.aggregationFields || [])
        .filter(fld =>
          // Ignore current Field.
          fld.fieldGuid !== this.tableField.fieldGuid &&
          // Field type must match current field.
          this.entityUtils.getFieldDataType(fld) == this.entityUtils.getFieldDataType(this.tableField) &&
          // Ignore RLS/Internal fields.
          !fld.isRLSField && !fld.isInternal
        ).map((fld) => {
          // Convert to Select Option: { value: guid, text: name }.
          return { value: fld.fieldGuid, text: fld.displayName + ' (' + this.entityUtils.getFieldDataTypeName(fld).toLowerCase() + ')' }
        });
    },

    /** Field to Aggregate - restricts available functions. */
    aggregationField() {
      return this.aggregationEntity?.fields.find(fld => fld.fieldGuid === this.calculationData.aggregationFieldGuid);
    },

    /** Table Fields to exclude from selector. */
    selectorTableFieldsToIgnore() {
      return this.aggregationFields
        .filter(fld =>
          // Exclude the current Field.
          fld.fieldGuid == this.tableField.fieldGuid ||
          // Exclude 'special' Fields.
          fld.isRLSField || fld.isInternal);
    },

    /** Row Fields to exclude from selector. */
    selectorRowFieldsToIgnore() {
      return this.table.fields.filter(fld => fld.fieldGuid == this.tableField.fieldGuid || fld.isRLSField || fld.isInternal);
    },

    // Fields available for Assistant to include in query.
    assistantQueryFields() {
      return (this.aggregationFields || [])
        // Ignore current Field & RLS fields.
        .filter((fld) => { return fld.fieldGuid !== this.tableField.fieldGuid && !fld.isRLSField; });
    },

    /** Additional (bespoke, non-standard) functions available for the Assistant to use in the formula. */
    assistantAdditionalFunctions() {
      // May want to include functions in the query.
      return [] // this.functionsToRender
        .filter(fn => fn.bespoke && (fn.variants || []).length)
        .map(fn => `${fn.name}(${(fn.variants[0].params || []).map(prm => prm.name).join(', ')}) [${fn.purpose}].`);
    },

  },

  methods: {

    // Formula Editor utilities.
    getformulaEditor() {
      return this.$refs.formulaEditor;
    },
    onFormulaChanged(formula) {
      this.filterFormula = formula;
      if (this.calculationDataLoaded)
        this.calculationData.filterFormula = this.entityUtils.changeFieldNamesToGuids(formula, this.aggregationEntity?.fields || [], this.table.fields);
    },
    replaceEditorTextSelection(text, cursorOffset = 0) {
      this.getformulaEditor().replaceTextSelection(text, cursorOffset);
    },
    clearformulaVerify() {
      return this.getformulaEditor().setFormulaVerify();
    },

    /** 
     * Basic validation on the Calculation to determine whether the formula can be verified.
     * - Returns a warning message to display below the formula editor in place of invoking the Verify logic.
     * - Called by Code Editor prior to verifying.
     */
    calculationWarningOnVerify(){
      if (!this.aggregationEntityGuid) return 'Aggregation Table must be selected';
      if (!this.calculationData.aggregationFieldGuid) return 'Aggregation Field must be selected';
      if (!this.calculationData.aggregateFunction) return 'Aggregate Function must be selected';
    },

    // Table Field selection.
    addTableFieldToFormula(field) {
      if (!field) return;
      this.replaceEditorTextSelection(`{{'${field.displayName}'}}`);
    },

    // Row Field selection.
    addRowFieldToFormula(field) {
      if (!field) return;
      this.replaceEditorTextSelection(`{{'ROW:${field.displayName}'}}`);
    },

    // Atlas Assistant.
    atlasAssistant() {
      return this.$refs.atlasAssistant;
    },
    askAssistantError(error) {
      this.atlasAssistant().askFormulaError(this.filterFormula, error);
      this.setFormulaVerify();
    },
    useAssistantFormula(formula) {
      this.filterFormula = this.entityUtils.changeFieldGuidsToNames(formula, this.aggregationEntity?.fields || [], this.table.fields);
    },

  },

  // Called right before the component is to be mounted.
  async beforeMount() {
    // Ensure that Calculation data is initialised.
    if (!this.calculationData.hasOwnProperty('aggregationFieldGuid'))
      this.$set(this.calculationData, 'aggregationFieldGuid', '');
    if (!this.calculationData.hasOwnProperty('aggregateFunction'))
      this.$set(this.calculationData, 'aggregateFunction', '');
    if (!this.calculationData.hasOwnProperty('filterFormula'))
      this.$set(this.calculationData, 'filterFormula', '');

    // Aggregation Field.
    if (this.calculationData.aggregationFieldGuid) {
      const aggregationField = await this.apiUtils.getField(this.calculationData.aggregationFieldGuid);
      this.aggregationEntityGuid = aggregationField?.entityGuid;
    };
  },

  // Called after the component has been mounted.
  mounted() {
  },

};

</script>

<style scoped lang="scss">
#formulaEditor {
  ::v-deep #codeEditor {
    height: 260px;
    max-height: 320px;
  }
}

.properties_group.field-selector {
  max-height: 320px;
}
</style>
